const EDGE_EPSILON = 1;
const BOUNCE_COOLDOWN_MS = 170;
const MIN_BOUNCE_DELTA = 1.4;

let _installed = false;
const _bounceState = new WeakMap();

function _getBounceInfo(host) {
    if (!host) return null;
    let info = _bounceState.get(host);
    if (!info) {
        info = { lastAt: {}, anim: null, edgeLock: { x: '', y: '' } };
        _bounceState.set(host, info);
    }
    return info;
}

function _isOverflowScrollable(value) {
    return value === 'auto' || value === 'scroll' || value === 'overlay';
}

function _getPageScrollHost() {
    return document.scrollingElement || document.documentElement;
}

function _closestScrollable(target, axis) {
    let node = target instanceof Element ? target : null;
    while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        if (axis === 'x') {
            const canScrollX = _isOverflowScrollable(style.overflowX) && node.scrollWidth - node.clientWidth > EDGE_EPSILON;
            if (canScrollX) return node;
        } else {
            const canScrollY = _isOverflowScrollable(style.overflowY) && node.scrollHeight - node.clientHeight > EDGE_EPSILON;
            if (canScrollY) return node;
        }
        node = node.parentElement;
    }
    return null;
}

function _edgeState(host, axis) {
    if (!host) return null;
    if (axis === 'x') {
        const max = Math.max(0, host.scrollWidth - host.clientWidth);
        return {
            max,
            atStart: host.scrollLeft <= EDGE_EPSILON,
            atEnd: host.scrollLeft >= max - EDGE_EPSILON,
        };
    }
    const max = Math.max(0, host.scrollHeight - host.clientHeight);
    return {
        max,
        atStart: host.scrollTop <= EDGE_EPSILON,
        atEnd: host.scrollTop >= max - EDGE_EPSILON,
    };
}

function _triggerEdgeBounce(host, axis, direction) {
    const visualHost = host === _getPageScrollHost()
        ? (document.querySelector('.app-layout') || document.body || document.documentElement)
        : host;
    if (!visualHost || !visualHost.animate) return;

    const info = _getBounceInfo(visualHost);
    if (!info) return;

    const edgeDirection = direction > 0 ? 'forward' : 'backward';
    if (info.edgeLock[axis] === edgeDirection) return;
    info.edgeLock[axis] = edgeDirection;

    const now = performance.now();
    const edgeKey = `${axis}:${edgeDirection}`;
    if (now - (info.lastAt[edgeKey] || 0) < BOUNCE_COOLDOWN_MS) return;
    info.lastAt[edgeKey] = now;

    if (info.anim && info.anim.playState === 'running') return;

    const amount = axis === 'x' ? 9 : 8;
    const dx = axis === 'x' ? (direction > 0 ? -amount : amount) : 0;
    const dy = axis === 'y' ? (direction > 0 ? -amount : amount) : 0;

    info.anim = visualHost.animate(
        [
            { transform: 'translate3d(0, 0, 0)' },
            { transform: `translate3d(${dx}px, ${dy}px, 0)` },
            { transform: 'translate3d(0, 0, 0)' },
        ],
        {
            duration: 180,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        }
    );
}

function _releaseEdgeLock(host, axis) {
    const visualHost = host === _getPageScrollHost() ? (document.body || document.documentElement) : host;
    const info = _getBounceInfo(visualHost);
    if (!info) return;
    info.edgeLock[axis] = '';
}

function _handleHorizontalWheel(e) {
    const host = _closestScrollable(e.target, 'x');
    if (!host) return false;

    const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    if (!delta) return false;

    const edges = _edgeState(host, 'x');
    if (!edges || edges.max <= EDGE_EPSILON) return false;
    if (!edges.atStart && !edges.atEnd) _releaseEdgeLock(host, 'x');

    if ((delta < 0 && edges.atStart) || (delta > 0 && edges.atEnd)) {
        if (Math.abs(delta) >= MIN_BOUNCE_DELTA) {
            _triggerEdgeBounce(host, 'x', delta);
        }
        e.preventDefault();
        return true;
    }

    host.scrollLeft += delta;
    e.preventDefault();
    return true;
}

function _handleVerticalEdgeBounce(e) {
    if (!e.deltaY) return;

    const host = _closestScrollable(e.target, 'y');
    if (host) {
        const edges = _edgeState(host, 'y');
        if (!edges || edges.max <= EDGE_EPSILON) return;
        if (!edges.atStart && !edges.atEnd) _releaseEdgeLock(host, 'y');
        if ((e.deltaY < 0 && edges.atStart) || (e.deltaY > 0 && edges.atEnd)) {
            if (Math.abs(e.deltaY) >= MIN_BOUNCE_DELTA) {
                _triggerEdgeBounce(host, 'y', e.deltaY);
            }
            e.preventDefault();
        }
        return;
    }

    const pageHost = _getPageScrollHost();
    const pageEdges = _edgeState(pageHost, 'y');
    if (!pageEdges || pageEdges.max <= EDGE_EPSILON) return;
    if (!pageEdges.atStart && !pageEdges.atEnd) _releaseEdgeLock(pageHost, 'y');
    if ((e.deltaY < 0 && pageEdges.atStart) || (e.deltaY > 0 && pageEdges.atEnd)) {
        if (Math.abs(e.deltaY) >= MIN_BOUNCE_DELTA) {
            _triggerEdgeBounce(pageHost, 'y', e.deltaY);
        }
        e.preventDefault();
    }
}

function _installWheelSystem() {
    if (_installed) return;
    _installed = true;

    document.addEventListener(
        'wheel',
        (e) => {
            if (e.defaultPrevented) return;
            if (_handleHorizontalWheel(e)) return;
            _handleVerticalEdgeBounce(e);
        },
        { passive: false }
    );
}

export function enableHorizontalWheelScroll() {
    _installWheelSystem();
}

export function enableProxyWheelScroll() {
    _installWheelSystem();
}

export function enablePageEdgeFeedback() {
    _installWheelSystem();
}

export function enableWorkflowWheelRouting() {
    _installWheelSystem();
}
